<template>
  <div class="tw-min-h-screen" style="background-color: #eaeed3;">
    <AppBar />
    <ScrollToTop />
    <main class="tw-overflow-x-hidden">

    <!-- ======================================
         HERO SECTION
    ======================================= -->
    <section class="tw-flex tw-flex-col lg:tw-flex-row tw-items-stretch" style="min-height: 540px;">
      <div class="tw-flex tw-flex-col tw-justify-center tw-px-8 lg:tw-px-16 tw-py-12 tw-flex-1">
        <h1 class="tw-uppercase tw-leading-tight" style="color: #346a7e; font-family: 'Roboto Condensed', sans-serif;">
          <span class="tw-block tw-font-bold" style="font-size: clamp(1.6rem, 3vw, 2.7rem);">Science-Based Encyclopedia</span>
          <span class="tw-block tw-font-bold" style="font-size: clamp(1.3rem, 2.4vw, 2.2rem);">of Nutrition and Natural Medicine</span>
          <span class="tw-block tw-font-normal tw-tracking-widest tw-mt-2" style="font-size: clamp(1rem, 1.8vw, 1.75rem);">
            Improving Health Through Knowledge
          </span>
        </h1>
        <p
          class="tw-mt-6 tw-leading-snug tw-max-w-xl tw-text-justify"
          style="font-family: 'Roboto Condensed', sans-serif; font-size: 1rem; font-weight: 400;"
        >
          Green Medicine Works is a comprehensive resource on human health, covering a wide range of topics related to various body ailments.
        </p>
        <p
          class="tw-mt-3 tw-leading-snug tw-max-w-xl tw-text-justify"
          style="font-family: 'Roboto Condensed', sans-serif; font-size: 1rem; font-weight: 400;"
        >
          Our goal at Green Medicine Works is to provide trustworthy, evidence-based information on natural health, helping people learn about the therapeutic benefits of vitamins, minerals, herbs, and foods, and how they support maintaining or achieving good health. We believe that knowledge is essential for empowering individuals to take control of their health and well-being.
        </p>
        <div class="tw-mt-8">
          <router-link to="/about" aria-label="Learn more about Green Medicine Works">
            <v-btn
              rounded="pill"
              size="large"
              class="tw-px-6"
              style="background-color: #2d6f41; color: white;"
              aria-label="Learn more about Green Medicine Works"
            >
              <v-icon>mdi-arrow-right</v-icon>
            </v-btn>
          </router-link>
        </div>
      </div>

      <div class="tw-w-full lg:tw-w-[52%] tw-relative lg:tw-min-h-[500px] tw-px-8 lg:tw-px-0 tw-pb-12 lg:tw-pb-0 tw-mt-8 lg:tw-mt-0">
        <div class="tw-w-full tw-h-[300px] lg:tw-absolute lg:tw-inset-0 lg:tw-h-auto">
          <HeroCarousel
            height="100%"
            class="tw-w-full tw-h-full tw-rounded-[30px] lg:tw-rounded-none lg:tw-rounded-l-[50px]"
            style="box-shadow: -8px 8px 32px rgba(0,0,0,0.18);"
          />
        </div>
      </div>
    </section>

    <!-- ======================================
         WHY GREEN MEDICINE WORKS
    ======================================= -->
    <section class="tw-px-6 lg:tw-px-20 tw-py-16">
      <h2
        class="tw-font-semibold tw-text-center tw-mb-6"
        style="color: #346a7e; font-family: 'Roboto Condensed', sans-serif; font-size: clamp(1.3rem, 2.5vw, 2.2rem);"
      >
        Trusted Knowledge, Natural Solutions. Better Health
      </h2>
      <p
        class="tw-text-justify tw-leading-8 tw-mb-2 tw-mx-auto"
        style="font-family: 'Roboto Condensed', sans-serif; font-size: 1.1rem; max-width: 79rem;"
      >
        You believe in the power of nature, but you're tired of navigating a world filled with vague claims and unproven "miracle" cures. You seek remedies that are both natural and effective, but you need to trust the source. Green Medicine Works is built for you. We are not just another herbal database; we are your definitive bridge between traditional wisdom and modern science.
      </p>
      <p
        class="tw-text-justify tw-leading-8 tw-mb-10 tw-mx-auto"
        style="font-family: 'Roboto Condensed', sans-serif; font-size: 1.1rem; max-width: 79rem;"
      >
        Here's why you should make us your trusted resource:
      </p>

      <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-3 tw-mx-auto" style="max-width: 56rem;">

        <!-- Top-left: outlined, only top-left corner rounded -->
        <!-- Top-left -->
        <ValuesGridCard
          theme="light"
          roundedCorner="top-left"
          iconUrl="/landing_page/psychiatry.svg"
          iconClass="!tw-w-12 !tw-h-12"
          title="We Bring Clarity To The Complexity Of Nature"
          description="We bring clarity to the complex world of natural medicine. Our mission is to provide unbiased, evidence-based profiles on botanicals, revealing what the scientific research actually says about their effectiveness and risks."
          descriptionClass="tw-text-justify tw-leading-8"
        />

        <!-- Top-right -->
        <ValuesGridCard
          theme="dark"
          roundedCorner="top-right"
          iconUrl="/landing_page/experiment.svg"
          iconClass="!tw-w-12 !tw-h-12"
          title="We Honor Tradition With Scientific Validation"
          description="While we respect centuries of traditional use, we believe true reliability comes from scientific validation. We investigate the clinical evidence behind each natural remedy to provide a complete, 360-degree view you can trust."
          descriptionClass="tw-text-justify tw-leading-8"
        />

        <!-- Bottom-left -->
        <ValuesGridCard
          theme="dark"
          roundedCorner="bottom-left"
          iconUrl="/landing_page/encrypted.svg"
          iconClass="!tw-w-12 !tw-h-12"
          title="Empower You To Make Confident Safe Decisions"
          description="Our goal is to provide the knowledge you need to act safely. We go beyond the basics to explain how and why a remedy works, offering critical data on dosages, side effects, and interactions for confident and effective use in your life."
          descriptionClass="tw-text-justify tw-leading-8"
        />

        <!-- Bottom-right -->
        <ValuesGridCard
          theme="light"
          roundedCorner="bottom-right"
          iconUrl="/landing_page/approval_delegation.svg"
          iconClass="!tw-w-12 !tw-h-12"
          title="We Are Your Single Source Of Truth"
          description="Stop searching through unreliable websites. Our trusted encyclopedia is your one-stop source for organized, in-depth information, saving you time and protecting you from misinformation."
          descriptionClass="tw-text-justify tw-leading-8"
        />

      </div>

      <p
        class="tw-text-justify tw-leading-8 tw-mt-10 tw-mx-auto"
        style="font-family: 'Roboto Condensed', sans-serif; font-size: 1.1rem; max-width: 79rem;"
      >
        Choose Green Medicine Works to move beyond the hype and guesswork. Choose us to build your health on a foundation of nature's wisdom, reinforced by the strength of scientific proof.
      </p>
    </section>

    <!-- ======================================
         INTEGRATION PROCESS
    ======================================= -->
    <section id="integration" class="tw-px-6 lg:tw-px-16 tw-pb-12" style="padding-top: 2.5rem;">
      <!-- Outer container — title overlaps the top border (fieldset-legend effect) -->
      <div
        class="tw-relative tw-border-2 tw-rounded-[30px] tw-mx-auto"
        style="border-color: #2d6f41; max-width: 88rem; padding: 2.5rem 2rem 2rem;"
      >
        <!-- Title sitting on the top border, centered -->
        <div
          class="tw-absolute tw-left-1/2 -tw-translate-x-1/2 tw-px-6"
          style="top: -1rem; background-color: #eaeed3;"
        >
          <span
            class="tw-font-semibold tw-whitespace-nowrap"
            style="color: #2d6f41; font-family: 'Roboto Condensed', sans-serif; font-size: 1.5rem;"
          >Integration Process</span>
        </div>

        <!--
          Ascending staircase: each card offset down by a different margin-top.
          Green (leftmost) sits lowest, Step 2 (rightmost) sits highest.
          Arrows are pinned to the same absolute vertical position as the
          centre of the green card so they appear horizontally level.
        -->
        <div class="tw-flex tw-flex-col md:tw-flex-row tw-gap-4" style="align-items: flex-start;">

          <!-- Green card — lowest, shortest (margin-top: 48px) -->
          <div
            class="tw-rounded-[30px] tw-p-8 tw-text-white tw-w-full md:tw-flex-1"
            style="background-color: #2d6f41; min-height: 250px; margin-top: 3rem; box-shadow: 0 4px 20px rgba(0,0,0,0.15);"
          >
            <h3
              class="tw-font-bold tw-text-center tw-mb-4"
              style="font-family: 'Roboto Condensed', sans-serif; font-size: 1.8rem;"
            >
              Integration Steps
            </h3>
            <p
              class="tw-text-justify tw-leading-8"
              style="font-family: 'Roboto Condensed', sans-serif; font-size: 1rem;"
            >
              These steps will guide you in the process of integrating Green Medicine Works into your own website.
            </p>
          </div>

          <!-- Arrow 1 — pinned to green card's vertical midpoint (3rem + 125px ≈ 173px) -->
          <div
            class="tw-hidden md:tw-flex tw-flex-shrink-0"
            style="align-self: flex-start; margin-top: calc(3rem + 105px);"
          >
            <v-icon size="36" style="color: #2d6f41;">mdi-arrow-right</v-icon>
          </div>

          <!-- Step 1 — middle height (margin-top: 24px) -->
          <div
            class="tw-bg-white tw-rounded-[30px] tw-p-8 tw-w-full md:tw-flex-1"
            style="min-height: 300px; margin-top: 1.5rem; box-shadow: 0 4px 20px rgba(0,0,0,0.10);"
          >
            <div class="tw-flex tw-items-baseline tw-gap-3 tw-mb-4">
              <span class="tw-font-bold" style="color: #2d6f41; font-size: 5rem; font-family: 'Roboto Condensed', sans-serif; line-height: 1;">1</span>
              <span class="tw-font-bold tw-text-black" style="font-family: 'Roboto Condensed', sans-serif; font-size: 1.8rem;">Create Website</span>
            </div>
            <p style="font-family: 'Roboto Condensed', sans-serif; font-size: 1rem; line-height: 1.9;">
              Click the Tab for New Website and fill out the form to get your integration code. On completion you will receive a code, which you need to copy.
            </p>
          </div>

          <!-- Arrow 2 — same vertical level as Arrow 1 -->
          <div
            class="tw-hidden md:tw-flex tw-flex-shrink-0"
            style="align-self: flex-start; margin-top: calc(3rem + 105px);"
          >
            <v-icon size="36" style="color: #2d6f41;">mdi-arrow-right</v-icon>
          </div>

          <!-- Step 2 — tallest, sits highest (margin-top: 0) -->
          <div
            class="tw-bg-white tw-rounded-[30px] tw-p-8 tw-w-full md:tw-flex-1"
            style="min-height: 350px; margin-top: 0; box-shadow: 0 4px 20px rgba(0,0,0,0.10);"
          >
            <div class="tw-flex tw-items-baseline tw-gap-3 tw-mb-4">
              <span class="tw-font-bold" style="color: #2d6f41; font-size: 5rem; font-family: 'Roboto Condensed', sans-serif; line-height: 1;">2</span>
              <span class="tw-font-bold tw-text-black" style="font-family: 'Roboto Condensed', sans-serif; font-size: 1.8rem;">Integration Action</span>
            </div>
            <p style="font-family: 'Roboto Condensed', sans-serif; font-size: 1rem; line-height: 1.9;">
              Place the generated code in Step 2 into the website page that will display the natural health content library. Then create your website links to that page.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- ======================================
         PLANS
    ======================================= -->
    <section id="plans" class="tw-px-6 lg:tw-px-16 tw-py-12">
      <div class="tw-text-center tw-mb-2">
        <span
          class="tw-font-semibold"
          style="color: #346a7e; font-family: 'Roboto Condensed', sans-serif; font-size: 2.2rem;"
        >Plans</span>
      </div>
      <p
        class="tw-text-center tw-font-normal"
        style="color: #2d6f41; font-family: 'Roboto Condensed', sans-serif; font-size: clamp(1.5rem, 3vw, 3.75rem);"
      >
        Choose the right plan for you
      </p>
      <p
        class="tw-text-center tw-mx-auto tw-mt-4 tw-mb-10"
        style="font-family: 'Roboto Condensed', sans-serif; font-size: 1.3rem; max-width: 51rem;"
      >
        Choose an affordable plan that's packed with the best features for engaging your audience, creating customer loyalty, and driving sales.
      </p>

      <div v-if="isLoadingPlans" class="tw-flex tw-justify-center tw-py-8">
        <v-progress-circular indeterminate color="#2d6f41" size="48" />
      </div>
      <div
        v-else
        class="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-mx-auto tw-gap-6"
        style="max-width: 64rem; align-items: stretch;"
      >
        <PricingCard
          v-for="tier in tiers"
          :key="tier.id"
          :tier="tier"
        />
      </div>
    </section>

    <!-- ======================================
         TESTIMONIALS
    ======================================= -->
    <section class="tw-px-6 lg:tw-px-16 tw-py-12">
      <div class="tw-rounded-[40px] tw-p-10 tw-mx-auto" style="background-color: rgba(45,111,65,0.1); max-width: 88rem;">

        <!-- Testimonial 1: Dr. Jacek — photo LEFT, text RIGHT -->
        <div class="tw-flex tw-flex-col md:tw-flex-row tw-gap-8 tw-mb-16 tw-items-start">

          <!-- Avatar: stacked decorative layers -->
          <div class="tw-flex-shrink-0 tw-relative" style="width: 210px; height: 210px;">
            <img
              src="/images/testimonials/bg-circle.svg"
              class="tw-absolute tw-inset-0 tw-w-full tw-h-full"
              width="236" height="231"
              alt=""
            />
            <img
              src="/images/testimonials/frame-ring.svg"
              class="tw-absolute"
              style="left: 2%; top: 13%; width: 86%; height: 86%;"
              width="210" height="210"
              alt=""
            />
            <img
              src="/images/testimonials/dr-jacek.webp"
              class="tw-absolute tw-inset-0 tw-w-full tw-h-full"
              style="border-radius: 50%; object-fit: cover;"
              width="420" height="420"
              alt="Dr. Jacek Giovanoli-Jakubczak"
            />
          </div>

          <!-- Quote + attribution -->
          <div class="tw-flex-1">
            <p
              class="tw-text-black tw-text-justify tw-leading-relaxed tw-mb-6"
              style="font-family: 'Roboto', sans-serif; font-size: 1rem; line-height: 1.75;"
            >
              I graduated from the Medical University of Warsaw in 1985. From 1988 to 1998, I worked in the Cardiac Intensive Care Unit. Since 1999, I have been running and co-owning the Vega Medica Institute of Holistic Medicine. I am a second-degree specialist in Internal Medicine. In my practice, I also employ: Homeopathy, Homotoxicology, Isopathic Medicine, Dark-Field Microscopy, Bioresonance, Oligoscan, Acupuncture, Herbal Medicine, and Homeosiniatry-administering drugs into acupuncture points. I have been working with GME for many years. I use GME every day. Green Medicine Encyclopedia is a vast database of natural medical knowledge! After entering the disease entity, it displays a list of possible treatment substances. The list is divided into groups such as microelements, vitamins, amino acids, fats, hormones, herbs, and foods. The most important part is that the sources of research on the substances "proposed" by GME are provided, and, most importantly for me, they can be printed for the patient so they can read them themselves and understand why I recommend them. Information about the dosage of individual preparations and the names of preparations available on the (American) market is included. GME is highly useful for doctors who, based on their medical knowledge, can select appropriate preparations, as well as for patients who want to support their health to prevent or reduce symptoms of an existing disease.<br /><br />
              Truly great work for patients and doctors.
            </p>
            <p class="tw-font-semibold" style="color: #234d5b; font-family: 'Roboto Condensed', sans-serif; font-size: 1.3rem;">Dr. Jacek</p>
            <p style="color: #234d5b; font-family: 'Roboto', sans-serif; font-size: 1rem;">Giovanoli-Jakubczak, MD</p>
            <p style="color: #234d5b; font-family: 'Roboto', sans-serif; font-size: 1rem;">Vega Medica Institute of Holistic Medicine</p>
            <a
              href="https://www.vegamedica.pl"
              target="_blank"
              rel="noopener noreferrer"
              class="tw-underline tw-font-bold"
              style="color: #234d5b; font-family: 'Roboto', sans-serif; font-size: 1rem;"
            >https://www.vegamedica.pl</a>
          </div>
        </div>

        <!-- Testimonial 2: Tammera — text LEFT, photo RIGHT, attribution right-aligned -->
        <div class="tw-flex tw-flex-col md:tw-flex-row tw-gap-8 tw-items-start">

          <!-- Quote + attribution (right-aligned) -->
          <div class="tw-flex-1 tw-text-right">
            <p
              class="tw-text-black tw-text-justify tw-leading-relaxed tw-mb-6"
              style="font-family: 'Roboto', sans-serif; font-size: 1rem; line-height: 1.75;"
            >
              "As a clinician, educator, researcher, and author, I find it essential to have quick access to reliable information. Few digital tools offer practitioners or consumers such a comprehensive collection of health-related data in one place as the Green Medicine Encyclopedia. This product offers personalization, notation, supporting references, and includes herbal, biochemical pathways, and alternative modality information, among other features. The Green Medicine Encyclopedia is a valuable resource; I have a copy on all our office computers and highly recommend it to nutrition professionals and students."
            </p>
            <p class="tw-font-semibold" style="color: #234d5b; font-family: 'Roboto Condensed', sans-serif; font-size: 1.3rem;">Tammera J. Karr,</p>
            <p style="color: #234d5b; font-family: 'Roboto Condensed', sans-serif; font-size: 1rem;">Ph.D., BCHN™, CGP, CNW</p>
            <p style="color: #234d5b; font-family: 'Roboto Condensed', sans-serif; font-size: 1rem;">Where Tradition Meets Science - Holistic Nutrition for the Whole You!</p>
            <a
              href="https://yourwholenutrition.com"
              target="_blank"
              rel="noopener noreferrer"
              class="tw-underline tw-font-bold"
              style="color: #234d5b; font-family: 'Roboto Condensed', sans-serif; font-size: 1rem;"
            >yourwholenutrition.com</a>
          </div>

          <!-- Avatar: stacked decorative layers -->
          <div class="tw-flex-shrink-0 tw-relative" style="width: 210px; height: 210px;">
            <img
              src="/images/testimonials/bg-circle.svg"
              class="tw-absolute tw-inset-0 tw-w-full tw-h-full"
              width="236" height="231"
              alt=""
            />
            <img
              src="/images/testimonials/frame-ring.svg"
              class="tw-absolute"
              style="left: 2%; top: 13%; width: 86%; height: 86%;"
              width="210" height="210"
              alt=""
            />
            <img
              src="/images/testimonials/tammera.webp"
              class="tw-absolute tw-inset-0 tw-w-full tw-h-full"
              style="border-radius: 50%; object-fit: cover;"
              width="420" height="420"
              alt="Tammera J. Karr"
            />
          </div>
        </div>

      </div>
    </section>

    <!-- ======================================
         CONTACT + BRAND
    ======================================= -->
    <section class="tw-px-6 lg:tw-px-16 tw-py-12">
      <div class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-6 tw-mx-auto" style="max-width: 88rem;">

        <!-- Contact Form -->
        <div class="tw-rounded-[40px] tw-p-8 tw-lg:tw-p-10" style="background-color: rgba(255,255,255,0.4); box-shadow: 0 4px 20px rgba(0,0,0,0.10);">
          <h2
            class="tw-font-normal tw-mb-8"
            style="color: #346a7e; font-family: 'Roboto', sans-serif; font-size: 2rem;"
          >
            Have a Question? Contact Us
          </h2>
          <v-form validate-on="submit" @submit.prevent="submit" ref="form">
            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="username"
                  label="Name"
                  type="text"
                  placeholder="Your name"
                  :rules="nameRules"
                  name="username"
                  variant="underlined"
                  color="#2d6f41"
                />
                <v-text-field
                  v-model="emailaddress"
                  label="Email"
                  type="email"
                  placeholder="Your email"
                  :rules="emailRules"
                  name="emailaddress"
                  variant="underlined"
                  color="#2d6f41"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-textarea
                  v-model="message"
                  label="Message"
                  placeholder="Your message"
                  :rules="messageRules"
                  name="message"
                  variant="underlined"
                  color="#2d6f41"
                  rows="4"
                />
                <div class="tw-flex tw-justify-end tw-mt-2">
                  <v-btn
                    type="submit"
                    rounded="pill"
                    :disabled="loading"
                    style="background-color: #2d6f41; color: white;"
                  >
                    <v-progress-circular v-if="loading" indeterminate size="18" color="white" class="tw-mr-2" />
                    <v-icon v-else class="tw-mr-1">mdi-send</v-icon>
                    Send
                  </v-btn>
                </div>
              </v-col>
            </v-row>
          </v-form>
        </div>

        <!-- Brand Info -->
        <div class="tw-rounded-[40px] tw-p-8 tw-lg:tw-p-10" style="background-color: rgba(255,255,255,0.4); box-shadow: 0 4px 20px rgba(0,0,0,0.10);">
          <p
            class="tw-font-normal tw-mb-3"
            style="color: #2d6f41; font-family: 'Roboto', sans-serif; font-size: 1.75rem;"
          >
            Green Medicine Works
          </p>
          <p
            class="tw-font-normal tw-mb-4"
            style="color: #346a7e; font-family: 'Roboto', sans-serif; font-size: 1.2rem;"
          >
            Natural Medicine for Better Health &amp; Wellness
          </p>
          <p
            class="tw-mb-6"
            style="color: #346a7e; font-family: 'Roboto', sans-serif; font-size: 0.9rem;"
          >
            Evidence Based Content Delivered to your Website or Physical Store Location
          </p>
          <ul
            class="tw-list-disc tw-pl-6 tw-space-y-1"
            style="color: #346a7e; font-family: 'Roboto', sans-serif; font-size: 0.9rem;"
          >
            <li>Increase Online Sales</li>
            <li>Increase Store Sales</li>
            <li>Employee Education</li>
            <li>Customer Education</li>
          </ul>
        </div>
      </div>
    </section>

    <!-- ======================================
         FOOTER
    ======================================= -->
    <footer class="tw-px-8 tw-pb-8 tw-pt-4" style="color: rgba(0,0,0,0.75); font-family: 'Roboto', sans-serif; font-size: 1.1rem;">
      {{ new Date().getFullYear() }} &copy; Green Medicine Works
    </footer>
    </main>
  </div>
</template>

<script setup lang="ts">
import { useSeo } from "@/composables/useSeo";
import { useToastStore } from "@/stores/toast";
import { onMounted, ref } from "vue";
import { VForm } from "vuetify/components";

const toastStore = useToastStore();
const form = ref<InstanceType<typeof VForm>>(null!);
const baseUrl = import.meta.env.VITE_API_URL;

useSeo({
  description:
    "Science Based Encyclopedia of Nutrition and Natural Health. Evidence-based content for better health & wellness.",
  keywords:
    "natural medicine, nutrition, health encyclopedia, wellness, evidence-based health",
});

// ── Contact form ──────────────────────────────────────────────────────────────

const loading = ref(false);
const username = ref<string | null>(null);
const emailaddress = ref<string | null>(null);
const message = ref<string | null>(null);
const nameRules = [(v: string) => !!v || "Name is required"];
const emailRules = [(v: string) => !!v || "Email is required"];
const messageRules = [(v: string) => !!v || "Message is required"];

async function submit() {
  const valid = await form.value?.validate();
  if (!valid?.valid) return;

  loading.value = true;
  const body = {
    r_from: emailaddress.value,
    r_subject: "Green Medicine Works | Contact",
    r_body: message.value,
    r_name: username.value,
    r_on_web: 1,
    r_title: "Contact",
  };
  try {
    const response = await fetch(`${baseUrl}/contact`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (response.status === 200) {
      const data = await response.json();
      username.value = "";
      emailaddress.value = "";
      message.value = "";
      toastStore.success(data.message);
    } else {
      const verror = JSON.parse(await response.text());
      toastStore.error({ text: verror.error?.detail ?? verror.error });
    }
  } catch (error) {
    if (error instanceof TypeError) toastStore.error({ text: error.message });
  } finally {
    loading.value = false;
  }
}

// ── Plans ─────────────────────────────────────────────────────────────────────

interface Tier {
  id: string;
  name: string;
  description: string;
  priceMonthly: string;
  features: string[];
  featured: boolean;
  is_coming: boolean;
  href: string;
}

const tiers = ref<Tier[]>([]);
const isLoadingPlans = ref(false);

onMounted(async () => {
  localStorage.setItem("user", "");
  await fetchPlans();
});

async function fetchPlans() {
  isLoadingPlans.value = true;

  let sessionId = "";
  try {
    const sessionRes = await fetch(`${baseUrl}/session`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    });
    if (sessionRes.status === 200) {
      const sessionData = await sessionRes.json();
      sessionId = sessionData.r_uuid ?? "";
    }
  } catch {
    // proceed without session
  }

  try {
    const res = await fetch(`${baseUrl}/subscription?r_is_trial=1`, {
      method: "GET",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    });
    if (res.status === 200) {
      const data = await res.json();
      tiers.value = data.map((item: any) => {
        const features: string[] = [item.r_content_1, item.r_content_2, item.r_content_3];
        if (item.r_content_4) features.push(item.r_content_4);
        return {
          id: item.r_id,
          name: item.r_subscription_lowername,
          description: item.r_description ?? "",
          priceMonthly: item.r_amount,
          features,
          featured: item.r_sorting === 2,
          is_coming: item.r_is_coming_value === 1,
          href: `/plans/${item.r_id}?data=${sessionId}`,
        };
      });
    }
  } catch (error) {
    console.error(error);
  } finally {
    isLoadingPlans.value = false;
  }
}
</script>

<!--
  Roboto Condensed and Inter are loaded asynchronously from index.html
  to avoid render-blocking. Do not re-import them here.
-->

<route lang="yaml">
meta:
  layout: non-auth.vue
  title: "Green Medicine Works"
  description: "Science Based Encyclopedia of Nutrition and Natural Health"
</route>
